import {getLocalStorage} from '@/Utils/CommonUtils';
import Environment from '../../../environment';
import {
  analyzeGameInBrowser,
  type BrowserAnalysisMode,
  fetchBrowserAnalysisPlyCount,
  getCachedBrowserAnalysis,
  isBrowserAnalysisSupported,
  isGameAlreadyAnalyzed,
} from './browserGameAnalysis';
import {StockfishEngineClient} from '@/Components/StockfishEngine/engine';
import {analyzeFenWithClient} from '@/Components/StockfishEngine/enginePool';
import {decideBrowserEligibility} from './analysisParity';
// import EventSource from 'react-native-event-source';

type AnalysisResponse = {
  game_id: number;
  status?: string;
  puzzle_summary?: any;
  success?: boolean;
  game_moves?: any[];
  error?: string;
  players: any;
  game_reviewed_by_user: boolean;
  game_result: string;
  game_engine_analyzed: boolean;
  total_moves: number;
  move_index: number;
  analysis: any;
  analysis_mode?: BrowserAnalysisMode;
  analysis_progress?: number;
  progress_message?: string;
};

/**
 * Overall safety cap for the in-browser path. A 67-ply game is ~68 s at
 * 1 s/move (FINDINGS §2); we allow generous headroom for long games + cold
 * load before falling back to the SSE so a slow device can't stall the screen
 * indefinitely.
 */
const BROWSER_OVERALL_TIMEOUT_MS = 240000; // spec §8.1 fallback cap
const DEEP_BROWSER_OVERALL_TIMEOUT_MS = 8 * 60 * 1000;
// Cap for fetching an already-stored analysis from start_analyzing_game so a
// stalled/streaming response can't pin the review screen on a loader forever.
const STORED_ANALYSIS_TIMEOUT_MS = 20000;
const STARTPOS_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

// import {Observable} from 'rxjs';

// export function streamGameAnalysis(
//   gameId: number,
// ): Observable<AnalysisResponse> {
//   return new Observable(observer => {
//     const url = `${Environment.CHESS_API_URL}/game/start_analyzing_game?game_id=${gameId}`;
//     const headers = {
//       'Content-Type': 'application/json',
//       'api-key': Environment.API_KEY,
//     };

//     const eventSource = new EventSource(url, {headers: headers});

//     eventSource.onmessage = event => {
//       const parsedData: AnalysisResponse = JSON.parse(event.data);
//       observer.next(parsedData); // Emit new data
//     };

//     eventSource.onerror = (error: Event) => {
//       console.log(error, 'error');
//       observer.error(error);
//       eventSource.close();
//     };

//     eventSource.onopen = () => {
//       // Handle any setup logic if needed
//     };

//     eventSource.onclose = () => {
//       observer.complete(); // Complete the observable when connection is closed
//     };

//     return () => {
//       eventSource.close(); // Cleanup: close the event source when observer is unsubscribed
//     };
//   });
// }
import {Observable} from 'rxjs';

let activeEventSource: EventSource | null = null;

/**
 * BACKEND FALLBACK: the original server-side SSE game-analysis stream
 * (`ANALYZE_GAME_API_URL`). Kept intact and used only when the in-browser
 * engine can't run the review (unsupported / load failure / per-ply error /
 * overall timeout). Emits `AnalysisResponse` objects identically to before.
 */
export function streamGameAnalysisViaSse(
  gameId: number,
): Observable<AnalysisResponse> {
  return new Observable(observer => {
    // If there's already an active event stream, close it
    if (activeEventSource) {
      activeEventSource.close();
      activeEventSource = null;
    }

    let cancelled = false;
    // Local handle so teardown can close THIS stream even if the module-level
    // `activeEventSource` has since been reassigned by another subscription.
    let localSource: EventSource | null = null;

    (async () => {
      const token = await getLocalStorage('access_token');
      if (cancelled) {
        return;
      }
      const url = `${Environment.ANALYZE_GAME_API_URL}?game_uuid=${gameId}&access_token=${token}`;

      localSource = new EventSource(url);
      activeEventSource = localSource;

      localSource.onmessage = event => {
        const parsedData: AnalysisResponse = JSON.parse(event.data);
        observer.next(parsedData); // Emit new data

        if (
          parsedData.status === 'error' ||
          parsedData.status === 'Game already analyzed'
        ) {
          localSource?.close();
          if (activeEventSource === localSource) {
            activeEventSource = null;
          }
          observer.complete();
        }
      };

      localSource.onerror = () => {
        observer.error(new Error('EventSource error'));
        localSource?.close();
        if (activeEventSource === localSource) {
          activeEventSource = null;
        }
      };
    })();

    // Teardown MUST be returned from the Observable factory (not the async
    // IIFE) so unsubscribing actually closes the SSE connection.
    return () => {
      cancelled = true;
      localSource?.close();
      if (activeEventSource === localSource) {
        activeEventSource = null;
      }
    };
  });
}

function streamStoredGameAnalysis(
  gameId: number,
): Observable<AnalysisResponse> {
  return new Observable(observer => {
    let cancelled = false;
    // start_analyzing_game returns the stored analysis as plain JSON for an
    // already-analyzed game, but the SAME url streams (SSE) when the server
    // disagrees about the analyzed flag. A hung/streaming body would make
    // `response.json()` never resolve and leave the screen on a spinner, so
    // bound the request and surface a timeout as an error to the caller.
    const controller =
      typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeoutHandle = setTimeout(() => {
      controller?.abort();
    }, STORED_ANALYSIS_TIMEOUT_MS);

    (async () => {
      try {
        const token = await getLocalStorage('access_token');
        const url = `${Environment.ANALYZE_GAME_API_URL}?game_uuid=${gameId}&access_token=${token}`;
        const response = await fetch(
          url,
          controller ? {signal: controller.signal} : undefined,
        );
        const data = await response.json();
        clearTimeout(timeoutHandle);
        if (cancelled) {
          return;
        }
        if (!response.ok || data?.error) {
          observer.error(
            new Error(data?.error || 'stored game analysis fetch failed'),
          );
          return;
        }

        if (data?.players_data) {
          observer.next(data.players_data);
        }
        if (data?.game_overview) {
          observer.next(data.game_overview);
        }
        observer.next({
          ...data,
          status: 'Game already analyzed',
          game_moves: data?.game_moves || [],
        });
        observer.complete();
      } catch (err) {
        clearTimeout(timeoutHandle);
        if (!cancelled) {
          observer.error(
            err instanceof Error
              ? err
              : new Error('stored game analysis fetch failed'),
          );
        }
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(timeoutHandle);
      controller?.abort();
    };
  });
}

/**
 * BROWSER-FIRST game analysis with backend fallback.
 *
 * When the in-browser SF18-lite engine is supported, run the whole game review
 * locally via `analyzeGameInBrowser` (same `AnalysisResponse` shape, so the
 * screen renders identically). Fall back to the server SSE on:
 *   - unsupported browser (no Web Worker),
 *   - engine load failure / per-ply error (observer.error from the browser obs),
 *   - overall per-game timeout (BROWSER_OVERALL_TIMEOUT_MS).
 *
 * The returned `Observable<AnalysisResponse>` contract is unchanged, so
 * `index.tsx` needs no changes.
 */
export function streamGameAnalysis(
  gameId: number,
  mode: BrowserAnalysisMode = 'normal',
  alreadyAnalyzed = false,
): Observable<AnalysisResponse> {
  return new Observable<AnalysisResponse>(observer => {
    let fellBack = false;
    let browserSub: {unsubscribe: () => void} | null = null;
    let fallbackSub: {unsubscribe: () => void} | null = null;
    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
    let emittedAny = false;
    let cancelled = false;

    const startFallback = (reason: string) => {
      if (fellBack) {
        return;
      }
      fellBack = true;
      if (timeoutHandle) {
        clearTimeout(timeoutHandle);
        timeoutHandle = null;
      }
      try {
        browserSub?.unsubscribe();
      } catch {
        /* noop */
      }
      console.log(
        `[gameAnalysis] browser path failed (${reason}) → SSE fallback`,
      );
      fallbackSub = streamGameAnalysisViaSse(gameId).subscribe({
        next: v => observer.next(v),
        error: e => observer.error(e),
        complete: () => observer.complete(),
      });
    };

    // Run a fresh in-browser analysis (with server SSE fallback). Used both as
    // the default path and as a recovery when a stored-result fetch fails.
    const runBrowserAnalysis = async () => {
      if (cancelled || fellBack) {
        return;
      }
      if (!isBrowserAnalysisSupported()) {
        startFallback('browser engine unsupported');
        return;
      }

      const [probeMs, plies] = await Promise.all([
        probeDevice(),
        fetchBrowserAnalysisPlyCount(gameId),
      ]);
      if (cancelled || fellBack) {
        return;
      }

      const nav =
        typeof navigator !== 'undefined'
          ? (navigator as Navigator & {deviceMemory?: number})
          : null;
      let decision = decideBrowserEligibility({
        hasWorker: true,
        probeMs,
        cores: nav?.hardwareConcurrency || 1,
        deviceMemory: nav?.deviceMemory,
        plies,
      });
      const pliesLabel = plies > 0 ? String(plies) : 'unknown';
      if (!decision.useBrowser && isLocalDevBrowser()) {
        decision = {useBrowser: true, K: 1, plyCap: 80, tier: 'low'};
        console.log(
          `[gameAnalysis] local/dev browser override probe=${probeMs}ms plies=${pliesLabel}`,
        );
      }
      if (!decision.useBrowser) {
        startFallback(
          `gate rejected tier=${decision.tier} probe=${probeMs}ms plies=${pliesLabel}`,
        );
        return;
      }

      console.log(
        `[gameAnalysis] using in-browser SF18-lite ${mode} analysis K=${decision.K} probe=${probeMs}ms plies=${pliesLabel}`,
      );
      const browserPoolSize =
        mode === 'deep' ? Math.max(1, Math.min(decision.K, 2)) : decision.K;

      timeoutHandle = setTimeout(
        () => {
          startFallback('overall timeout');
        },
        mode === 'deep'
          ? DEEP_BROWSER_OVERALL_TIMEOUT_MS
          : BROWSER_OVERALL_TIMEOUT_MS,
      );

      browserSub = analyzeGameInBrowser(
        gameId,
        browserPoolSize,
        mode,
      ).subscribe({
        next: v => {
          emittedAny = true;
          observer.next(v as AnalysisResponse);
        },
        error: err => {
          startFallback(
            (err && err.message) ||
              (emittedAny ? 'mid-stream error' : 'init error'),
          );
        },
        complete: () => {
          if (timeoutHandle) {
            clearTimeout(timeoutHandle);
            timeoutHandle = null;
          }
          if (!fellBack) {
            console.log('[gameAnalysis] in-browser analysis complete');
            observer.complete();
          }
        },
      });
    };

    (async () => {
      try {
        // 1) localStorage cache first: if this device already analyzed the game
        //    in-browser, replay it immediately — no re-analysis, and it works
        //    even when the DB lookup is slow. Normal mode only (deep recomputes).
        if (mode === 'normal') {
          const cached = await getCachedBrowserAnalysis(gameId, mode);
          if (cancelled) {
            return;
          }
          if (cached?.length) {
            console.log('[gameAnalysis] localStorage cache hit → replaying');
            cached.forEach(response => observer.next(response));
            observer.complete();
            return;
          }
        }

        // 2) Backend DB: a full server analysis (analyzed=1) or a previously
        //    stored browser analysis (analyzed=2) is returned as JSON by
        //    start_analyzing_game. Reuse it instead of re-analyzing. If the
        //    caller/list already knows the game is analyzed, do not run a new
        //    browser analysis if the stored fetch fails.
        const hasStoredAnalysis =
          mode === 'normal' &&
          (alreadyAnalyzed || (await isGameAlreadyAnalyzed(gameId)));
        if (mode === 'normal' && hasStoredAnalysis) {
          if (cancelled) {
            return;
          }
          console.log(
            '[gameAnalysis] backend already analyzed → using stored result',
          );
          fallbackSub = streamStoredGameAnalysis(gameId).subscribe({
            next: v => observer.next(v),
            error: err => {
              console.log(
                `[gameAnalysis] stored result fetch failed (${
                  err?.message || 'error'
                })`,
              );
              observer.error(err);
            },
            complete: () => observer.complete(),
          });
          return;
        }

        // 3) Nothing stored anywhere → run a fresh in-browser analysis.
        await runBrowserAnalysis();
      } catch (err) {
        startFallback(
          err instanceof Error ? err.message : 'browser gate/probe failed',
        );
      }
    })();

    return () => {
      cancelled = true;
      if (timeoutHandle) {
        clearTimeout(timeoutHandle);
      }
      try {
        browserSub?.unsubscribe();
      } catch {
        /* noop */
      }
      try {
        fallbackSub?.unsubscribe();
      } catch {
        /* noop */
      }
    };
  });
}

// Returned when the probe can't complete (warmup/probe error or hard timeout).
// Larger than every decideBrowserEligibility cutoff so the gate cleanly routes
// the device to the server tier instead of the probe throwing.
const PROBE_SLOW_SENTINEL_MS = 9999;

async function probeDevice(): Promise<number> {
  const engine = new StockfishEngineClient();
  try {
    await engine.init();
    engine.newGame();
    // Warmup (wasm compile / JIT) — not measured. If even this can't finish,
    // the device is too slow for the in-browser path.
    try {
      await analyzeFenWithClient(engine, STARTPOS_FEN, {
        depth: 4,
        multiPV: 1,
        timeoutMs: 4000,
      });
    } catch {
      return PROBE_SLOW_SENTINEL_MS;
    }
    const start = Date.now();
    try {
      await analyzeFenWithClient(engine, STARTPOS_FEN, {
        depth: 12,
        // `movetime` self-limits the probe but MUST stay above the gate's
        // slow-device cutoff (1200ms) so a slow device can reveal itself
        // (rather than being capped into a faster tier). `timeoutMs` MUST
        // exceed `movetime` so the search is bounded by movetime — not killed
        // by the pool's hard timeout, which would reject and lose the reading.
        movetime: 3000,
        multiPV: 1,
        timeoutMs: 6000,
      });
    } catch {
      return PROBE_SLOW_SENTINEL_MS;
    }
    return Date.now() - start;
  } catch {
    return PROBE_SLOW_SENTINEL_MS;
  } finally {
    engine.destroy();
  }
}

function isLocalDevBrowser(): boolean {
  const isDev = typeof __DEV__ !== 'undefined' && Boolean(__DEV__);
  const host =
    typeof window !== 'undefined' && window.location
      ? window.location.hostname
      : '';
  return (
    isDev || host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0'
  );
}
